import type { CSSProperties, RefObject } from "react";
import { useLayoutEffect, useRef } from "react";

/** Propriétés numériques CSS sans unité px implicite (aligné sur les conventions React). */
const CSS_NUMBER_UNITLESS = new Set<string>([
	"animationIterationCount",
	"borderImageOutset",
	"borderImageSlice",
	"borderImageWidth",
	"columnCount",
	"fillOpacity",
	"flex",
	"flexGrow",
	"flexShrink",
	"fontWeight",
	"gridColumnEnd",
	"gridColumnSpan",
	"gridColumnStart",
	"gridRowEnd",
	"gridRowSpan",
	"gridRowStart",
	"lineClamp",
	"lineHeight",
	"opacity",
	"order",
	"orphans",
	"strokeDasharray",
	"strokeDashoffset",
	"strokeMiterlimit",
	"strokeOpacity",
	"strokeWidth",
	"tabSize",
	"widows",
	"zIndex",
	"zoom",
]);

function camelToKebab(name: string): string {
	if (name.startsWith("--")) {
		return name;
	}
	return name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

function setStyleProperty(style: CSSStyleDeclaration, propName: string, value: string | number): void {
	if (propName.startsWith("--")) {
		style.setProperty(propName, String(value));
		return;
	}
	const cssKey = camelToKebab(propName);
	if (typeof value === "number" && value !== 0 && !CSS_NUMBER_UNITLESS.has(propName)) {
		style.setProperty(cssKey, `${value}px`);
		return;
	}
	style.setProperty(cssKey, String(value));
}

/**
 * Applique la couleur token (`--data-color`) et, le cas échéant, les styles additionnels
 * sur le nœud DOM sans prop `style` en JSX (évite les alertes webhint / Edge Tools).
 */
export function useTypographyDataColor(color: string | undefined, propStyle?: CSSProperties): RefObject<HTMLElement | null> {
	const ref = useRef<HTMLElement | null>(null);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		el.removeAttribute("style");
		if (color) {
			el.style.setProperty("--data-color", `var(${color})`);
		}
		if (propStyle) {
			for (const [key, value] of Object.entries(propStyle)) {
				if (value === undefined || value === null || value === false) continue;
				if (key === "cssText") continue;
				setStyleProperty(el.style, key, value as string | number);
			}
		}
	}, [color, propStyle]);

	return ref;
}
